import React, { createContext, useContext, useState, useEffect } from 'react';
import { ThemeProvider as MuiThemeProvider, createTheme } from '@mui/material/styles';

type ThemeMode = 'light' | 'dark';

interface ThemeContextType {
    mode: ThemeMode;
    toggleTheme: () => void;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

export const useThemeContext = () => {
    const context = useContext(ThemeContext);
    if (!context) {
        throw new Error('useThemeContext must be used within a ThemeProvider');
    }
    return context;
};

export const ThemeProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [mode, setMode] = useState<ThemeMode>(() => {
        const savedMode = localStorage.getItem('themeMode');
        return (savedMode as ThemeMode) || 'light';
    });

    useEffect(() => {
        localStorage.setItem('themeMode', mode);
    }, [mode]);

    const isDark = mode === 'dark';

    const theme = createTheme({
        palette: {
            mode,
            primary: {
                main: '#800000',
            },
        },
        components: {
            MuiAppBar: {
                styleOverrides: {
                    root: {
                        backgroundColor: '#800000',
                    },
                },
            },
            MuiButton: {
                styleOverrides: isDark
                    ? {
                          root: {
                              '&.MuiButton-outlined': {
                                  backgroundColor: '#2e2e2e',
                                  color: '#ffffff',
                                  borderColor: '#515151',
                              },
                              '&.MuiButton-outlined:hover': {
                                  backgroundColor: '#383838',
                                  borderColor: '#6b6b6b',
                              },
                          },
                      }
                    : {
                          root: {},
                      },
            },
            MuiOutlinedInput: {
                styleOverrides: isDark
                    ? {
                          root: {
                              backgroundColor: '#1f1f1f',
                              '& .MuiOutlinedInput-notchedOutline': {
                                  borderColor: '#515151',
                              },
                              '&:hover .MuiOutlinedInput-notchedOutline': {
                                  borderColor: '#6b6b6b',
                              },
                              '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                  borderColor: '#8a8a8a',
                              },
                          },
                      }
                    : {
                          root: {},
                      },
            },
            MuiInputLabel: {
                styleOverrides: isDark
                    ? {
                          root: {
                              color: '#cfcfcf',
                              '&.Mui-focused': {
                                  color: '#e0e0e0',
                              },
                          },
                      }
                    : {
                          root: {},
                      },
            },
        },
    });

    const toggleTheme = () => {
        setMode((prevMode) => (prevMode === 'light' ? 'dark' : 'light'));
    };

    return (
        <ThemeContext.Provider value={{ mode, toggleTheme }}>
            <MuiThemeProvider theme={theme}>
                {children}
            </MuiThemeProvider>
        </ThemeContext.Provider>
    );
}; 